import { convertISODateTimeToDate } from "app/utils/utils";
import axios from "axios";
export async function postJob(jobDetails) {
  return axios
    .post("/jobs", jobDetails)
    .then(function (response) {
      return response;
    })
    .catch(function (err) {
      throw Error(err);
    });
}

export async function deleteJob(id) {
  return axios.patch("/jobs/" + id, { jp_is_deleted: true }).catch((err) => {
    throw Error(err);
  });
}

export async function getJobCount(id, isDeleted) {
  const response = await axios.get(`/jobs/count`, {
    params: { companyId: id, isDeleted: isDeleted }
  });
  return response.data;
}

export async function restoreJob(id) {
  return axios.patch("/jobs/" + id, { jp_is_deleted: false }).catch((err) => {
    throw Error(err);
  });
}

export async function updateJob(jobDetails, id) {
  const res = await axios.put("/jobs/" + id, jobDetails);
  return res.data;
}

// Utility function to validate experience value
const validateExperience = (exp) => {
  if (exp === null || exp === "all") return null;
  const numExp = Number(exp);
  if (isNaN(numExp) || numExp < 0) {
    console.warn("Invalid experience value:", exp);
    return null;
  }
  return numExp;
};

export async function getJobListings(
  sort,
  column,
  limit,
  offset,
  searchQuery,
  id,
  type,
  exp,
  skill,
  location,
  city,
  state,
  salary,
  visibility,
  company,
  companyName,
  fetchCompanyLogo,
  isExpired
) {
  // Clean up and validate parameters
  const queryParams = {
    limit: limit,
    offset: offset || 0,
    searchQuery: searchQuery?.trim() || "", // For job title search
    orderBy: column || "",
    orderByValue: sort || "DESC",
    id: id || undefined,
    type: Array.isArray(type) ? type : [], // Ensure type is always an array
    exp: exp || null, // Experience range [min, max]
    skill: skill?.trim() || "",
    location: Array.isArray(location) ? location : [], // Ensure location is always an array
    salary: salary || null, // Salary range [min, max]
    city: city?.trim() || "",
    state: state?.trim() || "",
    companyName: companyName?.trim() || "", // For company name search
    visibility: visibility, // For job visibility filter
    company: company || undefined, // Company ID filter
    fetchCompanyLogo: fetchCompanyLogo || false,
    isExpired: isExpired || false
  };

  try {
    // Log the query parameters for debugging

    const response = await axios.get("/jobs", {
      params: queryParams
    });

    if (response.status === 204) {
      return { count: 0, data: [] };
    }

    const data = Object.values(response.data.rows).map((value) => {
      return {
        ...value,
        jp_created_at_raw: value.jp_created_at,
        jp_created_at: convertISODateTimeToDate(value.jp_created_at),
        jp_company_name: value.company.jp_company_name
      };
    });

    return {
      count: response.data.count,
      data: data
    };
  } catch (error) {
    console.error("Error fetching jobs:", error);
    return { count: 0, data: [] };
  }
}

export async function getJobListingByCompany(
  searchQuery,
  sort,
  column,
  limit,
  offset,
  id,
  type,
  exp,
  city,
  location,
  isExpired,
  isEnabled,
  startDate,
  endDate
) {
  const queryParams = {
    searchQuery,
    limit: limit,
    offset: offset,
    orderBy: column,
    orderByValue: sort,
    type: type,
    exp: exp,
    location: location,
    city: city,
    id: id,
    isExpired,
    isEnabled,
    startDate,
    endDate
  };
  const response = await axios.get("/jobs/company", {
    params: queryParams
  });
  let data = [];

  if (response.status !== 204) {
    data = Object.values(response.data.rows).map((value) => {
      delete value["jp_started_at"];
      return {
        ...value,
        jp_end_date_raw: value.jp_end_date,
        jp_start_date: convertISODateTimeToDate(value.jp_start_date),
        jp_end_date: convertISODateTimeToDate(value.jp_end_date),
        jp_created_at: convertISODateTimeToDate(value.jp_created_at)
      };
    });
    return { count: response.data.count, data: data };
  }

  return { count: 0, data: data };
}

export async function getDeletedJobs(sort, column, limit, offset, companyId, searchQuery, id) {
  const queryParams = {
    limit: limit,
    offset: offset,
    searchQuery: searchQuery,
    orderBy: column,
    orderByValue: sort,
    id: id,
    companyId: companyId
  };
  const response = await axios.get("/jobs/deleted", { params: queryParams });
  const data = response.data.rows.map((value) => ({
    ...value,
    jp_created_at: convertISODateTimeToDate(value.jp_created_at)
  }));
  return { rows: data, count: response.data.count };
}

export async function getCompanyJobSummary({ jobTitle, limit, offset, orderBy, orderByValue }) {
  const response = await axios.get("/jobs/company/summary", {
    params: {
      jobTitle,
      limit,
      offset,
      orderBy,
      orderByValue
    }
  });

  return response.data;
}

export async function getSingleJobListing(id, shouldFetchCompanyLogo = false) {
  const response = await axios.get(`/jobs/${id}`, {
    params: { fetchCompanyLogo: shouldFetchCompanyLogo }
  });
  return response.data;
}

export async function getCities(city) {
  const res = await axios.get("https://countriesnow.space/api/v0.1/countries/population/cities", {
    params: { city }
  });

  return res.data;
}

export async function sendTestEmailNotification(jobId, toEmail, updatedBy) {
  const res = await axios.post(`/jobposting_notification/${jobId}/verify-email`, {
    toEmail,
    updatedBy
  });
  return res.data;
}

export async function enableDisableJob(jobId, status, updatedBy) {
  const res = await axios.patch(`/jobs/${jobId}/status`, {
    status: status,
    updatedBy
  });
  return res.data;
}

export async function getAIRankedCandidates({
  jobId,
  candidateName,
  recommendation,
  limit,
  offset
}) {
  const res = await axios.get(`/jobs/${jobId}/applications`, {
    params: {
      candidateName,
      recommendation,
      limit,
      offset    
    }
  });
  return res.data;
}

export async function evaluateJobApplications({ jobId }) {
  const res = await axios.post(`/jobs/${jobId}/applications/evaluate`);
  return res.data;
}

export async function getRecommendedJobs(
  limit,
  offset,
  orderBy,
  orderByValue,
  fetchCompanyLogo = true
) {
  const res = await axios.get("/jobs/recommended", {
    params: {
      limit,
      offset,
      orderBy,
      orderByValue,
      fetchCompanyLogo
    }
  });
  return res.data;
}

export async function getApplicantCountForJob(jobId) {
  const res = await axios.get(`/jobs/${jobId}/application-count`);
  return res.data;
}

export async function getSimilarJobs(jobId, limit, offset, fetchCompanyLogo) {
  const res = await axios.get(`/jobs/${jobId}/similar`, {
    params: {
      limit,
      offset,
      fetchCompanyLogo
    }
  });
  return res.data;
}
